import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import Beacon from './Beacon.jsx';
import { IconExternal, IconLink, IconRefresh } from './icons.jsx';
import { Button } from './primitives.jsx';

export const META_RENEWAL_CONFIRMATION = Object.freeze({
  eyebrow: 'Meta authorization',
  title: 'Renew Meta authorization?',
  description: 'Continue to Meta using the same Meta account. Your current connection stays in place unless a verified replacement succeeds. Linked campaigns, destinations, Facebook Page and Instant Form assignments, approvals, and history are preserved. This does not publish, activate, or spend on ads.',
  confirmLabel: 'Continue to Meta',
  cancelLabel: 'Cancel',
  tone: 'accent',
});

// Guard the dialog, draft persistence, and navigation before React rerenders.
export function createMetaAuthorizationLauncher() {
  let inFlight = false;
  return async function launch({ credentialId, canManage, canReauthorize, requestConfirmation, isCurrent, start }) {
    if (inFlight || !canManage || (credentialId && !canReauthorize)) return null;
    inFlight = true;
    let navigating = false;
    try {
      if (credentialId && !(await requestConfirmation(META_RENEWAL_CONFIRMATION))) return null;
      if (!isCurrent()) return null;
      const result = await start();
      navigating = result?.started === true;
      return result;
    } finally {
      if (!navigating) inFlight = false;
    }
  };
}

export function metaRenewalRequired(payload, failures = []) {
  return ['reauth_required', 'disconnected'].includes(payload?.connection?.authorization?.state)
    || failures.some((failure) => failure?.code === 'meta_reauthorization_required');
}

export const META_COMPANY_STORAGE_KEY = 'lpam:meta-company-id';
export const META_RETURN_STORAGE_KEY = 'lpam:meta-return-to';
const META_CONNECTION_ENDPOINT = '/api/saas/meta/connection';
const LEGACY_REAUTH_STATES = new Set([
  'needs_reauth',
  'reauthorization_required',
  'meta_reauthorization_required',
]);
const DISCONNECTED_STATES = new Set(['disabled', 'deleted', 'disconnected']);
const AUTHORIZATION_STATES = new Set([
  'unknown',
  'checking',
  'valid',
  'expiring',
  'reauth_required',
  'disconnected',
]);
const AUTHORIZATION_PRESENTATIONS = Object.freeze({
  missing: Object.freeze({
    state: 'missing',
    label: 'Not connected',
    description: 'Connect the company’s Meta account before choosing campaign resources.',
    tone: 'neutral',
  }),
  unknown: Object.freeze({
    state: 'unknown',
    label: 'Authorization not checked',
    description: 'Check this Meta authorization before using provider-sensitive actions.',
    tone: 'neutral',
  }),
  checking: Object.freeze({
    state: 'checking',
    label: 'Checking connection',
    description: 'BeyondLeads is checking this Meta authorization in the background.',
    tone: 'neutral',
  }),
  valid: Object.freeze({
    state: 'valid',
    label: 'Connected',
    description: 'BeyondLeads can discover approved Meta resources for this company.',
    tone: 'live',
  }),
  expiring: Object.freeze({
    state: 'expiring',
    label: 'Connected—expires soon',
    description: 'Renew authorization before it expires. This keeps the same Meta integration and is separate from removal.',
    tone: 'warn',
  }),
  temporarily_unavailable: Object.freeze({
    state: 'temporarily_unavailable',
    label: 'Meta temporarily unavailable',
    description: 'Meta authorization could not be checked right now. The last known authorization state is preserved.',
    tone: 'warn',
  }),
  reauth_required: Object.freeze({
    state: 'reauth_required',
    label: 'Reconnect required',
    description: 'Meta authorization is no longer valid. Reauthorize this company’s Meta integration to continue.',
    tone: 'warn',
  }),
  disconnected: Object.freeze({
    state: 'disconnected',
    label: 'Disconnected',
    description: 'This Meta integration is disconnected. Reauthorize it before using Meta campaign resources.',
    tone: 'neutral',
  }),
  unhealthy: Object.freeze({
    state: 'unhealthy',
    label: 'Needs attention',
    description: 'Meta authorization status needs to be checked before using provider-sensitive actions.',
    tone: 'warn',
  }),
});
const REFRESH_CODES = new Set([
  'refresh_required',
  'meta_resource_refresh_required',
  'resource_refresh_required',
]);
const REFRESH_IN_PROGRESS_CODES = new Set([
  'meta_resource_reconciliation_in_progress',
  'resource_reconciliation_in_progress',
]);
const MAX_RETRY_AFTER_SECONDS = 30;
const META_SYNC_POLL_INTERVAL_MS = 2_000;
const META_SYNC_MAX_POLL_ATTEMPTS = 30;
// OGC-483: while a refresh waits on Meta's rate limit, check back around the
// retry time instead of every two seconds, never more than five minutes apart.
const META_SYNC_WAITING_POLL_MAX_MS = 5 * 60_000;
const META_SYNC_WAITING_POLL_MARGIN_MS = 5_000;
const CAMPAIGN_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function metaCredentialId(connection) {
  return text(connection?.credentialId || connection?.id);
}

function rawConnectionState(connection) {
  return text(
    connection?.connectionState
    || connection?.state
    || connection?.status,
  ).toLowerCase();
}

function authorizationFor(connection) {
  return connection?.authorization && typeof connection.authorization === 'object'
    && !Array.isArray(connection.authorization)
    ? connection.authorization
    : null;
}

export function curatedMetaAuthorizationPresentation(payload = {}) {
  const connection = payload?.connection || null;
  if (!connection || !metaCredentialId(connection)) return AUTHORIZATION_PRESENTATIONS.missing;

  const authorization = authorizationFor(connection);
  const authorizationState = text(authorization?.state).toLowerCase();
  if (authorization && AUTHORIZATION_STATES.has(authorizationState)) {
    if (
      authorizationState !== 'checking'
      && authorizationState !== 'reauth_required'
      && authorizationState !== 'disconnected'
      && text(authorization.lastCheckOutcome).toLowerCase() === 'temporarily_unavailable'
    ) {
      return AUTHORIZATION_PRESENTATIONS.temporarily_unavailable;
    }
    return AUTHORIZATION_PRESENTATIONS[authorizationState];
  }

  const legacyState = rawConnectionState(connection);
  if (LEGACY_REAUTH_STATES.has(legacyState)) return AUTHORIZATION_PRESENTATIONS.reauth_required;
  if (DISCONNECTED_STATES.has(legacyState)) return AUTHORIZATION_PRESENTATIONS.disconnected;
  return AUTHORIZATION_PRESENTATIONS.unknown;
}

// Transient inspection failures affect the warning/action presentation, but
// they do not revoke an already-proved authorization. Provider-sensitive
// operations must continue to follow the authoritative raw lifecycle state.
export function curatedMetaAuthorizationUsable(payload = {}) {
  const authorization = authorizationFor(payload?.connection || null);
  return ['valid', 'expiring'].includes(
    text(authorization?.state).toLowerCase(),
  );
}

export function curatedMetaResourceCatalog(payload = {}) {
  const credentialId = metaCredentialId(payload?.connection);
  const resources = payload?.resources;
  if (Array.isArray(resources?.connections)) {
    return resources.connections.find((candidate) => (
      metaCredentialId(candidate) === credentialId
    )) || null;
  }
  return resources && typeof resources === 'object' ? resources : null;
}

export function curatedMetaRetryAfterSeconds(payload = {}) {
  const catalog = curatedMetaResourceCatalog(payload);
  const value = catalog?.retryAfterSeconds ?? payload?.retryAfterSeconds;
  return Number.isSafeInteger(value) && value > 0
    ? Math.min(value, MAX_RETRY_AFTER_SECONDS)
    : MAX_RETRY_AFTER_SECONDS;
}

// OGC-483: the recorded time a refresh paused by a Meta rate limit retries,
// or null once it has passed (or when nothing is waiting on Meta).
export function curatedMetaRateLimitedUntil(payload = {}, now = Date.now()) {
  const catalog = curatedMetaResourceCatalog(payload);
  const retryAt = Date.parse(catalog?.rateLimitedUntil || '');
  return Number.isFinite(retryAt) && retryAt > now ? new Date(retryAt) : null;
}

export function curatedMetaConnectionState(payload = {}, { now = Date.now() } = {}) {
  const authorization = curatedMetaAuthorizationPresentation(payload);
  const catalog = curatedMetaResourceCatalog(payload);
  const syncState = text(catalog?.syncState).toLowerCase();
  // A refresh Meta paused is neither stuck nor failed: it retries on its own.
  if (curatedMetaAuthorizationUsable(payload) && curatedMetaRateLimitedUntil(payload, now)) {
    return 'waiting_on_meta';
  }
  if (
    curatedMetaAuthorizationUsable(payload)
    && ['failed', 'degraded'].includes(syncState)
  ) {
    return 'sync_failed';
  }
  if (authorization.state !== 'valid') return authorization.state;

  const connection = payload?.connection || null;
  const readinessCode = text(
    catalog?.readinessCode
    || connection.readinessCode
    || connection.resultCode
    || payload?.code
    || payload?.resources?.readinessCode,
  ).toLowerCase();
  const refreshInProgress = (
    ['queued', 'running'].includes(syncState)
    ||
    catalog?.refreshInProgress === true
    || REFRESH_IN_PROGRESS_CODES.has(readinessCode)
  );
  if (refreshInProgress) return 'refresh_in_progress';

  if (['failed', 'degraded'].includes(syncState)) {
    return 'sync_failed';
  }

  const refreshRequired = connection.refreshRequired === true
    || catalog?.refreshRequired === true
    || payload?.resources?.refreshRequired === true
    || REFRESH_CODES.has(readinessCode);
  if (refreshRequired) return 'refresh_required';
  return 'connected';
}

export function curatedMetaConnectionPresentation(payload = {}, { now = Date.now() } = {}) {
  const state = curatedMetaConnectionState(payload, { now });
  if (state === 'waiting_on_meta') {
    const retryAt = curatedMetaRateLimitedUntil(payload, now);
    const minutes = Math.max(1, Math.ceil((retryAt.valueOf() - now) / 60_000));
    const at = retryAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return Object.freeze({
      state,
      label: 'Connected — waiting on Meta',
      description: `Meta is limiting requests right now. Resource refresh is deferred until about ${at} (in about ${minutes} min). Completion has not been confirmed.`,
      tone: 'live',
    });
  }
  if (AUTHORIZATION_PRESENTATIONS[state]) return AUTHORIZATION_PRESENTATIONS[state];
  if (state === 'connected') return AUTHORIZATION_PRESENTATIONS.valid;
  if (state === 'refresh_required') return Object.freeze({
    state,
    label: 'Connected — refresh needed',
    description: 'Saved Meta resources need an update. Completion has not been confirmed.',
    tone: 'live',
  });
  if (state === 'refresh_in_progress' && text(curatedMetaResourceCatalog(payload)?.syncState).toLowerCase() === 'queued') return Object.freeze({
    state,
    label: 'Connected — refresh queued',
    description: 'A Meta resource refresh is queued. Waiting for synchronization to start; completion has not been confirmed.',
    tone: 'live',
  });
  if (state === 'refresh_in_progress') return Object.freeze({
    state,
    label: 'Connected — updating resources',
    description: 'BeyondLeads is updating approved Meta resources in the background.',
    tone: 'live',
  });
  return Object.freeze({
    state: 'sync_failed',
    label: 'Sync needs attention',
    description: 'Meta remains connected. A company owner or admin can retry resource synchronization.',
    tone: 'warn',
  });
}

// Display text only. Never feed this projection into authorization, readiness,
// resource selection or action capabilities.
export function curatedMetaObservationPresentation(payload = {}, { exhausted = false, now = Date.now() } = {}) {
  const presentation = curatedMetaConnectionPresentation(payload, { now });
  const pending = ['checking', 'refresh_required', 'refresh_in_progress', 'waiting_on_meta'].includes(presentation.state);
  if (!pending || !exhausted) return Object.freeze({ ...presentation, observationExhausted: false });
  return Object.freeze({
    state: 'observation_unconfirmed',
    label: presentation.state === 'checking' ? 'Authorization check unconfirmed'
      : presentation.state === 'waiting_on_meta' ? 'Connected — waiting on Meta' : 'Connected — update unconfirmed',
    description: `${presentation.state === 'waiting_on_meta' ? `${presentation.description} ` : 'The requested Meta update has not been confirmed complete. '}Automatic status checks have stopped. Check status for the latest update.`,
    tone: 'warn',
    observationExhausted: true,
  });
}

export function curatedMetaConnectionLabel(payload = {}) {
  return curatedMetaConnectionPresentation(payload).label;
}

export function curatedMetaConnectionAction(payload = {}, capabilities = payload?.capabilities || {}) {
  if (capabilities?.canManage !== true) return null;
  const state = curatedMetaConnectionState(payload);
  if (state === 'missing') return Object.freeze({ kind: 'connect', label: 'Connect Meta', busyLabel: 'Opening Meta' });
  if (state === 'expiring') {
    return capabilities?.canReauthorize === true
      ? Object.freeze({ kind: 'reauthorize', label: 'Renew Meta authorization', busyLabel: 'Opening Meta' })
      : null;
  }
  if (['reauth_required', 'disconnected'].includes(state)) {
    return capabilities?.canReauthorize === true
      ? Object.freeze({ kind: 'reauthorize', label: 'Renew Meta authorization', busyLabel: 'Opening Meta' })
      : null;
  }
  if (
    ['unknown', 'temporarily_unavailable', 'unhealthy'].includes(state)
    && capabilities?.canCheckAuthorization === true
  ) {
    return Object.freeze({
      kind: 'check_authorization',
      label: state === 'temporarily_unavailable' ? 'Check again' : 'Check authorization',
      busyLabel: 'Checking connection',
    });
  }
  if (state === 'sync_failed' && capabilities?.canRefresh === true) {
    return Object.freeze({ kind: 'refresh', label: 'Retry sync', busyLabel: 'Retrying sync' });
  }
  return null;
}

export function curatedMetaPollingDecision(payload = {}, count = 0, { now = Date.now() } = {}) {
  const safeCount = Number.isSafeInteger(count) ? Math.max(0, count) : 0;
  const state = curatedMetaConnectionState(payload, { now });
  if (state === 'waiting_on_meta') {
    const untilRetryMs = curatedMetaRateLimitedUntil(payload, now).valueOf() - now;
    const shouldPoll = safeCount < META_SYNC_MAX_POLL_ATTEMPTS;
    return Object.freeze({
      shouldPoll,
      nextCount: shouldPoll ? safeCount + 1 : 0,
      delayMs: shouldPoll
        ? Math.min(Math.max(untilRetryMs + META_SYNC_WAITING_POLL_MARGIN_MS, META_SYNC_POLL_INTERVAL_MS), META_SYNC_WAITING_POLL_MAX_MS)
        : 0,
    });
  }
  // A stale-first read queues work after building its response, so its initial
  // projection can still say refresh_required. Poll that bounded pre-queue
  // state as well as the durable queued/running state.
  const active = ['checking', 'refresh_required', 'refresh_in_progress'].includes(state);
  const shouldPoll = active && safeCount < META_SYNC_MAX_POLL_ATTEMPTS;
  return Object.freeze({
    shouldPoll,
    nextCount: shouldPoll ? safeCount + 1 : 0,
    delayMs: shouldPoll ? META_SYNC_POLL_INTERVAL_MS : 0,
  });
}

export function curatedMetaIdentityRows(connection = {}) {
  const safeConnection = connection && typeof connection === 'object' ? connection : {};
  const summary = safeConnection.maskedSummary && typeof safeConnection.maskedSummary === 'object'
    ? safeConnection.maskedSummary
    : {};
  const identity = safeConnection.identity && typeof safeConnection.identity === 'object'
    ? safeConnection.identity
    : {};
  const rows = [
    ['Meta profile', identity.name || summary.metaUserName, identity.id || summary.metaUserId],
    ['Business', summary.businessName, summary.businessId],
    ['Ad account', summary.accountName || summary.adAccountName, summary.adAccountId],
  ];
  return rows
    .filter(([, name, id]) => text(name) || text(id))
    .map(([label, name, id]) => ({
      label,
      value: [text(name), text(id)].filter(Boolean).join(' · '),
    }));
}

export function safeCuratedMetaReturnPath(value) {
  if (['company', '/#/company', '#/company', '/#/company/connections', '#/company/connections'].includes(value)) return '/#/company/connections';
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.surface === 'company') return '/#/company/connections';
    if (['campaign_meta', 'campaign_review'].includes(value.surface)) {
      const campaignId = text(value.campaignId);
      return CAMPAIGN_ID_PATTERN.test(campaignId)
        ? `/#/curated-campaign/${encodeURIComponent(campaignId)}?step=${value.surface === 'campaign_review' ? 'review' : 'meta'}`
        : '/#/company/connections';
    }
    return '/#/company/connections';
  }
  const match = text(value).match(/^\/?#\/curated-campaign\/([0-9a-f-]+)\?step=(meta|review)$/i);
  return match
    ? CAMPAIGN_ID_PATTERN.test(match[1])
      ? `/#/curated-campaign/${encodeURIComponent(match[1])}?step=${match[2].toLowerCase()}`
      : '/#/company/connections'
    : '/#/company/connections';
}

export function safeAuthorizationUrl(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    throw new Error('Meta sign-in could not be opened safely.');
  }
  const hostname = url.hostname.toLowerCase();
  if (url.protocol !== 'https:' || (hostname !== 'facebook.com' && !hostname.endsWith('.facebook.com'))) {
    throw new Error('Meta sign-in could not be opened safely.');
  }
  return url.toString();
}

export async function startCuratedMetaOAuth({
  companyId,
  returnSurface,
  campaignId = '',
  credentialId = '',
  isCurrent = () => true,
  request = (path, body, options) => api.post(path, body, options),
  storage = typeof sessionStorage === 'undefined' ? null : sessionStorage,
  navigate = (url) => window.location.assign(url),
  origin = typeof window === 'undefined' ? '' : window.location.origin,
} = {}) {
  const normalizedCompanyId = text(companyId);
  if (!normalizedCompanyId || !['company', 'campaign_meta', 'campaign_review'].includes(returnSurface)) {
    throw new Error('Meta sign-in could not be started safely.');
  }
  if (['campaign_meta', 'campaign_review'].includes(returnSurface) && !text(campaignId)) {
    throw new Error('Save this campaign before connecting Meta.');
  }
  const redirectUri = new URL('/meta/callback', origin).toString();
  const response = await request(META_CONNECTION_ENDPOINT + '/connect-url', {
    redirectUri,
    returnSurface,
    ...(returnSurface !== 'company' ? { campaignId: text(campaignId) } : {}),
    ...(text(credentialId) ? { credentialId: text(credentialId) } : {}),
  }, tenantHeaders(normalizedCompanyId));
  const authorizationUrl = safeAuthorizationUrl(response?.url);
  if (typeof isCurrent !== 'function' || !isCurrent()) {
    return Object.freeze({ started: false });
  }
  if (!storage?.setItem || typeof navigate !== 'function') {
    throw new Error('Meta sign-in could not be opened safely.');
  }
  storage.setItem(META_COMPANY_STORAGE_KEY, normalizedCompanyId);
  try {
    storage.setItem(META_RETURN_STORAGE_KEY, JSON.stringify({
      companyId: normalizedCompanyId,
      returnTo: safeCuratedMetaReturnPath({ surface: returnSurface, campaignId }),
    }));
    navigate(authorizationUrl);
  } catch (error) {
    storage.removeItem?.(META_COMPANY_STORAGE_KEY);
    storage.removeItem?.(META_RETURN_STORAGE_KEY);
    throw error;
  }
  return Object.freeze({ started: true });
}

export function storedCuratedMetaReturnPath(storage, companyId) {
  try {
    const context = JSON.parse(storage?.getItem(META_RETURN_STORAGE_KEY) || 'null');
    return context?.companyId === companyId ? safeCuratedMetaReturnPath(context.returnTo) : '/#/company/connections';
  } catch {
    return '/#/company/connections';
  }
}

export async function refreshCuratedMetaConnection({ companyId, credentialId } = {}) {
  const normalizedCredentialId = text(credentialId);
  if (!text(companyId) || !normalizedCredentialId) {
    throw new Error('Meta resources cannot be refreshed without a connection.');
  }
  const result = await api.post(
    `/api/saas/meta/connections/${encodeURIComponent(normalizedCredentialId)}/resources/refresh`,
    {},
    tenantHeaders(text(companyId)),
  );
  return requireCuratedMetaRefreshSuccess(result);
}

export async function checkCuratedMetaAuthorization({
  companyId,
  credentialId,
  request = (path, body, options) => api.post(path, body, options),
} = {}) {
  const normalizedCompanyId = text(companyId);
  const normalizedCredentialId = text(credentialId);
  if (!normalizedCompanyId || !normalizedCredentialId) {
    throw new Error('Meta authorization cannot be checked without a connection.');
  }
  const result = await request(
    `/api/saas/meta/connections/${encodeURIComponent(normalizedCredentialId)}/authorization-check`,
    {},
    tenantHeaders(normalizedCompanyId),
  );
  if (!['authorization_check_queued', 'authorization_check_coalesced'].includes(result?.outcome)) {
    throw new Error('Meta authorization check could not be confirmed.');
  }
  return Object.freeze({
    outcome: result.outcome,
    checking: result.checking === true,
  });
}

// OGC-417. A retry whose budget is spent is now answered with a recovered
// attempt lineage instead of a refusal, and it arrives here as a third success
// outcome. Without this it fell through to the fallback below and was reported
// as meta_resource_reconciliation_failed -- a successful recovery painted as
// "campaign resources need another refresh", which is the same silent-failure
// shape the ticket exists to remove.
export const CURATED_META_SYNC_RECOVERED_OUTCOME = 'sync_lineage_recovered';
const CURATED_META_SYNC_QUEUED_OUTCOMES = new Set([
  'sync_queued',
  'sync_coalesced',
  CURATED_META_SYNC_RECOVERED_OUTCOME,
]);

export function curatedMetaSyncQueued(result) {
  return CURATED_META_SYNC_QUEUED_OUTCOMES.has(result?.outcome);
}

function recoveryCount(value) {
  return Number.isSafeInteger(value) && value >= 0 && value <= 100 ? value : null;
}

// The one place the recovery is described to a tenant, so the company panel and
// the campaign wizard cannot drift into two different accounts of the same
// bounded action. Returns null for an ordinary queued sync, leaving each
// surface's existing success copy untouched.
export function curatedMetaSyncRecoveryNotice(result) {
  if (result?.outcome !== CURATED_META_SYNC_RECOVERED_OUTCOME) return null;
  const used = recoveryCount(result.recoveriesUsed);
  const remaining = recoveryCount(result.recoveriesRemaining);
  const budget = used !== null && remaining !== null
    ? ` This used sync recovery ${used} of ${used + remaining} for this update.`
    : '';
  const exhausted = remaining === 0
    ? ' No further recovery is available for it; contact support if this update fails again.'
    : '';
  return `Meta synchronization recovered. A new attempt is running in the background.${budget}${exhausted}`;
}

export function requireCuratedMetaRefreshSuccess(result = {}) {
  if (result?.outcome === 'fully_reconciled') return result;
  if (
    curatedMetaSyncQueued(result)
    && ['queued', 'running'].includes(result?.syncState)
  ) return result;
  const fallbackCode = result.outcome === 'refresh_in_progress'
    ? 'meta_resource_reconciliation_in_progress'
    : result.outcome === 'reauthorization_required'
      ? 'meta_reauthorization_required'
      : 'meta_resource_reconciliation_failed';
  const code = typeof result.code === 'string' && /^meta_[a-z0-9_]{1,80}$/.test(result.code)
    ? result.code
    : fallbackCode;
  const error = new Error('Meta resource refresh requires attention.');
  error.code = code;
  error.outcome = result.outcome;
  error.retryable = result.retryable === true;
  if (Number.isSafeInteger(result.retryAfterSeconds) && result.retryAfterSeconds > 0) {
    error.retryAfterSeconds = Math.min(result.retryAfterSeconds, MAX_RETRY_AFTER_SECONDS);
  }
  throw error;
}

export async function disconnectCuratedMetaConnection({ companyId, credentialId, retireUnusedSetup = false } = {}) {
  const normalizedCredentialId = text(credentialId);
  if (!text(companyId) || !normalizedCredentialId) {
    throw new Error('Meta cannot be removed without an active integration.');
  }
  // OGC-529: retiring an unused company setup is a separate explicit opt-in.
  return api.del(
    `${META_CONNECTION_ENDPOINT}/${encodeURIComponent(normalizedCredentialId)}`,
    retireUnusedSetup === true
      ? { ...tenantHeaders(text(companyId)), body: { retireUnusedSetup: true } }
      : tenantHeaders(text(companyId)),
  );
}

// OGC-533: every reload or poll briefly resets the connection to loading.
// Readiness and gates keep the last settled authorization for the same
// company meanwhile, so they never flicker to Ready or drop a block.
export function useSettledMetaAuthorizationState(metaConnection, companyId) {
  const last = useRef({ companyId: '', state: '' });
  const status = metaConnection?.status;
  const state = status === 'ready' ? curatedMetaAuthorizationPresentation(metaConnection.payload).state
    : ['error', 'idle'].includes(status) ? 'unavailable' : 'loading';
  if (state !== 'loading') {
    last.current = { companyId, state };
    return state;
  }
  return last.current.companyId === companyId && last.current.state ? last.current.state : 'loading';
}

export function useCuratedMetaConnection({ companyId, identityKey = '', enabled = true, requestSync = true } = {}) {
  const scope = JSON.stringify([text(companyId), text(identityKey)]);
  const [snapshot, setSnapshot] = useState(() => ({
    scope: '', readNonce: -1, status: enabled && companyId ? 'loading' : 'idle',
    payload: { connection: null, resources: null, capabilities: {} },
    displayOnlyPresentation: null, observationExhausted: false, error: null,
  }));
  const [read, setRead] = useState({ scope, nonce: 0, automatic: false, statusOnly: false });
  const requestVersion = useRef(0);
  const polling = useRef({ scope: '', credentialId: '', count: 0 });

  useEffect(() => {
    const version = ++requestVersion.current;
    const normalizedCompanyId = text(companyId);
    const automatic = read.scope === scope && read.automatic;
    const statusOnly = read.scope === scope && read.statusOnly;
    // Only prose survives an automatic same-identity poll. All authoritative
    // data and capabilities are cleared while the new read is pending.
    setSnapshot(previous => ({
      scope, readNonce: read.nonce, status: enabled && normalizedCompanyId ? 'loading' : 'idle',
      payload: { connection: null, resources: null, capabilities: {} },
      displayOnlyPresentation: enabled && automatic && previous.scope === scope && previous.status === 'ready'
        ? previous.displayOnlyPresentation : null,
      observationExhausted: false, error: null,
    }));
    if (!enabled || !normalizedCompanyId) return undefined;
    const controller = new AbortController();
    let pollTimer = null;
    if (polling.current.scope !== scope) polling.current = { scope, credentialId: '', count: 0 };
    const allowSync = requestSync && !automatic && !statusOnly;
    api.get(META_CONNECTION_ENDPOINT + (allowSync ? '' : '?requestSync=false'), {
      ...tenantHeaders(normalizedCompanyId), signal: controller.signal,
    }).then(response => {
      if (version !== requestVersion.current) return;
      const responseCapabilities = response?.capabilities && typeof response.capabilities === 'object'
        ? response.capabilities
        : {
            canManage: response?.canManage === true, canRefresh: response?.canRefresh === true,
            canCheckAuthorization: response?.canCheckAuthorization === true,
            canReauthorize: response?.canReauthorize === true, canRemove: response?.canRemove === true,
          };
      const payload = { connection: response?.connection || null, resources: response?.resources || null, capabilities: responseCapabilities };
      const credentialId = metaCredentialId(payload.connection);
      if (polling.current.credentialId && polling.current.credentialId !== credentialId) polling.current.count = 0;
      polling.current.credentialId = credentialId;
      const poll = curatedMetaPollingDecision(payload, polling.current.count);
      const stillPending = curatedMetaPollingDecision(payload, 0).shouldPoll;
      const exhausted = stillPending && (statusOnly || !poll.shouldPoll);
      setSnapshot({ scope, readNonce: read.nonce, status: 'ready', payload,
        displayOnlyPresentation: curatedMetaObservationPresentation(payload, { exhausted }),
        observationExhausted: exhausted, error: null,
      });
      if (!stillPending) polling.current.count = 0;
      if (poll.shouldPoll && !statusOnly) {
        polling.current.count = poll.nextCount;
        pollTimer = setTimeout(() => {
          if (version === requestVersion.current) setRead(current => ({ scope, nonce: current.nonce + 1, automatic: true, statusOnly: false }));
        }, poll.delayMs);
      }
    }).catch(error => {
      if (version !== requestVersion.current || controller.signal.aborted) return;
      setSnapshot({ scope, readNonce: read.nonce, status: 'error', payload: { connection: null, resources: null, capabilities: {} },
        displayOnlyPresentation: null, observationExhausted: false, error });
    });
    return () => { requestVersion.current += 1; controller.abort(); if (pollTimer) clearTimeout(pollTimer); };
  }, [companyId, scope, enabled, read, requestSync]);

  const reload = useCallback(() => {
    polling.current = { scope, credentialId: '', count: 0 };
    setRead(current => ({ scope, nonce: current.nonce + 1, automatic: false, statusOnly: false }));
  }, [scope]);
  // One existing status read, never another refresh request or an unbounded
  // observation restart. A terminal response naturally clears exhaustion.
  const checkStatus = useCallback(() => setRead(current => ({ scope, nonce: current.nonce + 1, automatic: false, statusOnly: true })), [scope]);
  const current = enabled && Boolean(text(companyId)) && snapshot.scope === scope;
  const sameRead = current && snapshot.readNonce === read.nonce;
  const pendingPresentation = current && read.scope === scope && read.automatic && snapshot.status === 'ready'
    ? snapshot.displayOnlyPresentation : null;
  const status = current && sameRead ? snapshot.status : enabled && companyId ? 'loading' : 'idle';
  return useMemo(() => ({
    companyId: text(companyId), status,
    error: sameRead ? snapshot.error : null,
    payload: sameRead ? snapshot.payload : { connection: null, resources: null, capabilities: {} },
    connection: sameRead ? snapshot.payload.connection : null,
    resources: sameRead ? snapshot.payload.resources : null,
    capabilities: sameRead ? snapshot.payload.capabilities : {},
    displayOnlyPresentation: sameRead ? snapshot.displayOnlyPresentation : pendingPresentation,
    observationExhausted: sameRead && snapshot.observationExhausted,
    statusCheckInProgress: current && read.scope === scope && read.statusOnly && status === 'loading',
    reload, checkStatus,
  }), [companyId, current, sameRead, status, read, scope, pendingPresentation, reload, checkStatus, snapshot]);
}

export function CuratedMetaConnectionActions({
  payload,
  canManage = false,
  canRefresh = false,
  canCheckAuthorization = false,
  canReauthorize,
  busy = '',
  onConnect,
  onCheckAuthorization,
  onRefresh,
}) {
  const state = curatedMetaConnectionState(payload);
  const credentialId = metaCredentialId(payload?.connection);
  const reauthorizationAllowed = canReauthorize === true
    || (canReauthorize === undefined && payload?.capabilities?.canReauthorize === true);
  const action = curatedMetaConnectionAction(payload, {
    canManage,
    canRefresh,
    canCheckAuthorization,
    canReauthorize: reauthorizationAllowed,
  });
  const actionHandler = ['connect', 'reauthorize'].includes(action?.kind)
    ? onConnect
    : action?.kind === 'check_authorization'
      ? onCheckAuthorization
      : action?.kind === 'refresh'
        ? onRefresh
        : null;
  const canRenderAction = Boolean(action && typeof actionHandler === 'function');
  const canRenew = credentialId && reauthorizationAllowed && typeof onConnect === 'function'
    && action?.kind !== 'reauthorize';
  if (!canManage || (!canRenderAction && !canRenew)) return null;

  const actionBusy = action?.kind === 'refresh'
    ? busy === 'refresh'
    : action?.kind === 'check_authorization'
      ? busy === 'check'
      : busy === 'connect';

  return (
    <div style={actionRowStyle}>
      {canRenderAction && (
        <Button type="button" variant="accent" size="sm" disabled={Boolean(busy)} onClick={actionHandler} aria-busy={actionBusy ? 'true' : undefined}>
          {actionBusy
            ? <Beacon size="inline" className="is-current" delayMs={0} />
            : action.kind === 'connect'
              ? <IconLink size={14} />
              : ['check_authorization', 'refresh'].includes(action.kind)
                ? <IconRefresh size={14} />
                : <IconExternal size={14} />}
          {actionBusy ? `${action.busyLabel}…` : action.label}
        </Button>
      )}
      {canRenew && (
        <Button type="button" variant="ghost" size="sm" disabled={Boolean(busy)} onClick={onConnect} aria-busy={busy === 'connect' ? 'true' : undefined}>
          {busy === 'connect' ? <Beacon size="inline" className="is-current" delayMs={0} /> : <IconExternal size={14} />} {busy === 'connect' ? 'Opening Meta…' : 'Renew Meta authorization'}
        </Button>
      )}
    </div>
  );
}

const actionRowStyle = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
